import type { ClaveApi } from '../../api/ops/clavesApi';
import type { PermisoCatalogo } from '../../api/ops/equipo';
import type { EstadoChip } from '../../componentes/Chip';
import { diaMes } from '../../logica/fechasCortas';

/**
 * Reglas de «Para desarrolladores» (13.6) sin estilos: estados de una clave, tipos que se pueden crear, solapamientos
 * de la rotación y los permisos agrupados por módulo. El panel no decide qué permisos ofrece: son los efectivos de
 * quien la crea (G37, G612), y su texto sale de `listPermissions` si la persona puede leerlo.
 */
export const CLAVE_ESTADO: Record<ClaveApi['status'], { texto: string; chip: EstadoChip }> = {
    active: { texto: 'Activa', chip: 'confirmed' },
    rotating: { texto: 'En rotación', chip: 'pending' },
    revoked: { texto: 'Revocada', chip: 'problem' },
    expired: { texto: 'Caducada', chip: 'problem' },
};

export const TIPOS_CLAVE: readonly { valor: 'private' | 'test'; titulo: string; texto: string }[] = [
    { valor: 'private', titulo: 'Privada', texto: 'Para un sistema tuyo o de un integrador. Trabaja con tus datos reales.' },
    { valor: 'test', titulo: 'De pruebas', texto: 'Contra un negocio de demostración. Para probar sin tocar nada real.' },
];

/** Cuánto sigue funcionando la antigua al rotar (`overlap_hours`, 0 a 168; 24 por defecto). */
export const SOLAPAMIENTOS: readonly { valor: string; texto: string }[] = [
    { valor: '24', texto: '24 horas (lo normal)' },
    { valor: '72', texto: '3 días' },
    { valor: '168', texto: '7 días (lo máximo)' },
    { valor: '0', texto: 'No: deja de funcionar ya' },
];

/** Revocada o caducada: ya no funciona (filtro «Revocadas y caducadas»). */
export function esCerrada(c: Pick<ClaveApi, 'status'>): boolean {
    return c.status === 'revoked' || c.status === 'expired';
}

export interface GrupoPermisos {
    modulo: string;
    nombre: string;
    permisos: { codigo: string; texto: string }[];
}

/** Nombre legible del módulo de un permiso: el del catálogo o, si no, el principio del código. */
export function nombreModulo(codigo: string, catalogo: readonly PermisoCatalogo[] | null): string {
    const modulo = catalogo?.find((p) => p.code === codigo)?.module ?? codigo.split('.')[0] ?? codigo;
    return modulo.charAt(0).toLocaleUpperCase('es') + modulo.slice(1);
}

/** Los permisos agrupados por módulo, en el orden del catálogo (los que no están en él, al final y por código). */
export function gruposDePermisos(codigos: readonly string[], catalogo: readonly PermisoCatalogo[] | null): GrupoPermisos[] {
    const orden = new Map((catalogo ?? []).map((p, i) => [p.code, i]));
    const lista = [...codigos].sort((a, b) => (orden.get(a) ?? Number.MAX_SAFE_INTEGER) - (orden.get(b) ?? Number.MAX_SAFE_INTEGER) || a.localeCompare(b));
    const grupos = new Map<string, GrupoPermisos>();
    for (const codigo of lista) {
        const p = catalogo?.find((x) => x.code === codigo);
        const modulo = p?.module ?? codigo.split('.')[0] ?? codigo;
        const grupo = grupos.get(modulo) ?? { modulo, nombre: nombreModulo(codigo, catalogo), permisos: [] };
        grupo.permisos.push({ codigo, texto: p?.description ?? codigo });
        grupos.set(modulo, grupo);
    }
    return [...grupos.values()];
}

const IPV4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})(?:\/(\d{1,2}))?$/;
const IPV6 = /^[0-9a-f:]+(?:\/(\d{1,3}))?$/i;

/** ¿Son todas direcciones IP (v4 o v6, con o sin máscara)? La API lo vuelve a comprobar. */
export function ipsValidas(ips: readonly string[]): boolean {
    return ips.every((ip) => {
        const v4 = IPV4.exec(ip);
        if (v4 !== null) {
            return v4.slice(1, 5).every((n) => Number(n) <= 255) && (v4[5] === undefined || Number(v4[5]) <= 32);
        }
        const v6 = IPV6.exec(ip);
        return v6 !== null && ip.includes(':') && (v6[1] === undefined || Number(v6[1]) <= 128);
    });
}

/** «Revocada el 3 sept» (G639); sin fecha, solo «Revocada». */
export function textoRevocada(c: Pick<ClaveApi, 'revoked_at'>): string {
    return c.revoked_at == null ? 'Revocada' : `Revocada el ${diaMes(c.revoked_at)}`;
}

/** Cuándo empieza una ventana del uso, en la zona del negocio (G643, G656): «28 oct, 10:00» por horas, «28 oct» por días. */
export function inicioDeVentana(iso: string, ventana: 'hour' | 'day', zona: string): string {
    const opciones: Intl.DateTimeFormatOptions = ventana === 'hour' ? { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' } : { day: 'numeric', month: 'short' };
    try {
        return new Intl.DateTimeFormat('es-ES', { ...opciones, timeZone: zona }).format(new Date(iso));
    } catch {
        return new Intl.DateTimeFormat('es-ES', opciones).format(new Date(iso));
    }
}

/** Lo que dice el gráfico del uso: «Por horas, las últimas 24 · hora de Madrid» o «Por días, las dos últimas semanas». */
export function subtituloDelUso(ventana: 'hour' | 'day', zona: string): string {
    const lugar = (zona.split('/').pop() ?? zona).replace(/_/g, ' ');
    return ventana === 'hour' ? `Por horas, las últimas 24 · hora de ${lugar}` : 'Por días, las dos últimas semanas';
}

/** Dirección de la API v1 (G125: se enseña sin `https://` y se copia entera): la del panel si no hay otra configurada. */
export function direccionApi(origenConfigurado: string, origenPanel: string): string {
    return `${origenConfigurado === '' ? origenPanel : origenConfigurado}/v1`;
}
